import { Modal, Page, Text } from '@ui/themed';
import { t } from 'i18next';
import { useState } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { useHandlers, withPageProviders } from '../../../context/page-context';
import { CompanyForm } from '../components/company-form';
import { useCompany, useDeleteCompany } from '../hooks/use-companies';
import type { CompanyFormValues } from '../schemas';
import { companyToFormValues } from '../transformers';

const COMPANIES_LIST_PATH = '/customers/companies';

const createDefaultValues: CompanyFormValues = {
  name: '',
  external_id: '',
  integration_id: '',
  accounting_id: '',
  customer_group: '',
  price_lists: [],
  sales_agent_groups: [],
  primary_customer_id: '',
  company_admin_ids: [],
  payment_on_account_enabled: false,
  payment_on_account: {
    credit_limit: '',
    balance: '',
    currency_code: '',
    net_terms: '',
  },
};

export const CompanyFormPage = withPageProviders(CompanyFormPageContent);

function CompanyFormPageContent() {
  const { navigationHandler, notificationHandler } = useHandlers();
  const { id } = navigationHandler.params();
  const isCreate = id === undefined || id === 'create';
  const companyId = !isCreate ? id : undefined;

  const { data: company, isLoading, isError, refetch } = useCompany(companyId);
  const deleteCompany = useDeleteCompany();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const handleDelete = () => {
    if (!companyId) return;
    deleteCompany.mutate(companyId, {
      onError: () => {
        notificationHandler.error();
      },
      onSuccess: () => {
        setDeleteModalOpen(false);
        notificationHandler.success(t('notifications:deleted'));
        navigationHandler.navigate(COMPANIES_LIST_PATH);
      },
    });
  };

  const pageTitle = isCreate
    ? t('features:companies.pages.company_form_page.create_title')
    : t('features:companies.pages.company_form_page.edit_title', { name: company?.name ?? '' });

  const secondaryActions =
    !isCreate && company
      ? [
          {
            onClick: () => setDeleteModalOpen(true),
            label: t('buttons:delete'),
            icon: 'delete' as const,
            danger: true,
          },
        ]
      : undefined;

  return (
    <Page
      narrow
      title={pageTitle}
      backUrl={COMPANIES_LIST_PATH}
      secondaryActions={secondaryActions}
      description={t('features:companies.pages.company_form_page.description')}
    >
      <SparkSuspense
        isLoading={isLoading && !isCreate}
        isError={isError && !isCreate}
        errorMessage={t('errors:generic')}
        onReload={() => void refetch()}
      >
        <CompanyForm
          defaultValues={isCreate || !company ? createDefaultValues : companyToFormValues(company)}
          isCreate={isCreate}
          companyId={companyId}
          seedPrimaryCustomer={company?.primary_customer}
          seedAdmins={company?.company_admins}
        />

        <Modal
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          primaryAction={{
            text: t('buttons:delete'),
            danger: true,
            loading: deleteCompany.isPending,
            onClick: handleDelete,
          }}
          secondaryAction={{
            text: t('buttons:cancel'),
            onClick: () => setDeleteModalOpen(false),
          }}
        >
          <Modal.Header>
            {t('features:companies.pages.company_form_page.delete_confirm_title')}
          </Modal.Header>
          <Modal.Body>
            <Text>
              {t('features:companies.pages.company_form_page.delete_confirm_body', {
                name: company?.name ?? '',
              })}
            </Text>
          </Modal.Body>
        </Modal>
      </SparkSuspense>
    </Page>
  );
}
